// Platform-wide lobby announcement banner. Rendered at the top of
// every game's lobby when `enabled === true`. The text + tone come
// from the platform_config.lobby_banner row, set by an admin via
// PUT /api/admin/platform/lobby_banner. Each game's controller
// includes it in `/api/<game>/config` so the lobby can render it
// without a separate endpoint round-trip.
//
// Design: ONE component shared across all 4 games so wording / styling
// stays identical. The host page just passes the props from its
// fetched config; this component handles the empty / disabled cases.
//
// Tone palette:
//   info     — neutral blue, e.g. "New tournament starting Friday"
//   warning  — amber, e.g. "Server maintenance in 30 minutes"
//   critical — red, e.g. "Voice chat temporarily unavailable"

import type { ReactNode } from 'react';

export type LobbyBannerProps = {
  enabled?: boolean;
  text?: string;
  tone?: 'info' | 'warning' | 'critical';
  // Optional override slot for embedding game-specific overlays beside
  // the banner copy (rarely used; default null).
  trailing?: ReactNode;
};

export default function LobbyBanner(p: LobbyBannerProps) {
  if (!p.enabled || !p.text || !p.text.trim()) return null;
  const tone = p.tone || 'info';
  // Per-tone classes. Kept inline so this component has no dependency
  // on any game's CSS file.
  const palette =
    tone === 'critical' ? {
      bg: 'bg-rose-900/85',
      bd: 'border-rose-500',
      tx: 'text-rose-50',
      ic: '⚠',
    } :
    tone === 'warning' ? {
      bg: 'bg-amber-900/85',
      bd: 'border-amber-500',
      tx: 'text-amber-50',
      ic: '!',
    } : {
      bg: 'bg-sky-900/85',
      bd: 'border-sky-500',
      tx: 'text-sky-50',
      ic: 'ⓘ',
    };
  return (
    <div
      role="status"
      className={
        'mx-3 mt-2 mb-1 px-3 py-2 rounded-lg border ' +
        'flex items-start gap-2 text-[12px] sm:text-[13px] font-medium leading-snug ' +
        palette.bg + ' ' + palette.bd + ' ' + palette.tx
      }
      style={{ boxShadow: '0 6px 16px rgba(0,0,0,0.35)' }}
    >
      <span
        className="inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white/15 text-[11px] font-bold"
        aria-hidden
      >
        {palette.ic}
      </span>
      <span className="flex-1 whitespace-pre-wrap break-words">{p.text}</span>
      {p.trailing}
    </div>
  );
}
